import { addWeeks, format, parseISO, startOfISOWeek } from 'date-fns';
import type { SprintResponse } from '@/api/api';

/**
 * Default workload window: current ISO Monday → +5 weeks. Used as the fallback
 * when a project has no sprints (so no derivable span), and by components that
 * want to seed a fresh range picker.
 */
export function defaultWorkloadWindow(now: Date = new Date()): { from: string; to: string } {
  const start = startOfISOWeek(now);
  return {
    from: format(start, 'yyyy-MM-dd'),
    to: format(addWeeks(start, 5), 'yyyy-MM-dd'),
  };
}

/**
 * The full-project workload window, derived from the project's sprints.
 *   `from` = ISO Monday on/before the earliest sprint `start_date`.
 *   `to`   = ISO Monday on/before the latest sprint `end_date` — guaranteeing
 *            the week containing the final day is part of the returned grid.
 *
 * Sprints are normally ordered by `number` ascending, but we scan for the
 * min start / max end so the result doesn't depend on that contract.
 * Returns `null` when there are no sprints — callers fall back to
 * `defaultWorkloadWindow()`.
 */
export function projectSpanWindow(
  sprints: SprintResponse[],
): { from: string; to: string } | null {
  if (sprints.length === 0) return null;

  let earliest = parseISO(sprints[0]!.start_date);
  let latest = parseISO(sprints[0]!.end_date);
  for (const sprint of sprints) {
    const start = parseISO(sprint.start_date);
    const end = parseISO(sprint.end_date);
    if (start < earliest) earliest = start;
    if (end > latest) latest = end;
  }

  return {
    from: format(startOfISOWeek(earliest), 'yyyy-MM-dd'),
    to: format(startOfISOWeek(latest), 'yyyy-MM-dd'),
  };
}
